import React, {
	useState, useEffect, useMemo, useCallback, useContext, useRef,
} from 'react';
import useSWR from 'swr';
import {
	Box, Text, Button, Icon, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, PopoverCloseButton, Checkbox,
	Stack, useBreakpointValue, ButtonGroup, IconButton, Menu, MenuButton, MenuList, MenuItem, VisuallyHidden,
} from '@chakra-ui/react';
import { FontAwesomeIcon as Fa } from '@fortawesome/react-fontawesome';
import { faCaretDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import {
	FlexibleXYPlot, HorizontalGridLines, VerticalGridLines, XAxis, YAxis, Hint,
	HorizontalBarSeries, VerticalBarSeries, Crosshair, AreaSeries, LineMarkSeries, makeVisFlexible,
	Sunburst, Treemap, RadialChart,
} from 'react-vis';
import { ResponsivePie } from '@nivo/pie';
import hash from 'object-hash';
import html2canvas from 'html2canvas';
import chroma from 'chroma-js';
import stable from 'stable';
import GraphContainer from './Container';
import theme from '../theme';
import { ContextStore } from '../../contexts/authContext';
import {
	str2Color, generateUnit as _generateUnit, sleep, bigNum2Chinese,
} from '../../utils';
import { USER_GROUP } from '../../utils/constants';
import { saveGraphPdf } from '../../utils/pdf';

const colors = ['primary', 'graph2', 'graph3', 'graph4', 'graph5', 'graph6', 'graph7', 'graph8', 'graph9', 'graph10'].map((x) => theme.colors[x]);

function buildValue(hoveredCell, xkey, ykey, unit = '') {
	if (!hoveredCell) return { x: 0, y: 0, title: '' };
	const {
		radius, angle, angle0, depth,
	} = hoveredCell;
	const truedAngle = (angle + angle0) / 2;

	let size = 0;
	if (depth === 2) {
		size = hoveredCell[ykey];
	} else if (depth === 1) {
		size = hoveredCell.children.reduce((acc, child) => acc + child[ykey], 0);
	}

	return {
		x: radius * Math.cos(truedAngle),
		y: radius * Math.sin(truedAngle),
		title: `${hoveredCell[xkey]}(${size}${unit ? ` ${unit}` : ''})`,
	};
}

function formatValue(v) {
	if (typeof v === 'number') {
		return v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	}
	if (v === undefined || v === null) return '';
	return v.toString();
}

function normalizeUnit(unit) {
	if (!unit) return '';
	return ` ${unit}`;
}

function buildLineBarAltText({
	title, data = [], yKeys = [], yUnit = [],
}) {
	const lines = [];
	data.forEach((dataset, datasetIndex) => {
		const label = yKeys[datasetIndex] || '數值';
		const unit = normalizeUnit(yUnit[datasetIndex]);
		dataset.forEach((point) => {
			if (point?.x === undefined || point?.y === undefined) return;
			lines.push(`${point.x} 的 ${label} 是 ${formatValue(point.y)}${unit}`);
		});
	});

	if (!lines.length) {
		return `這張圖是說明關於${title}的統計狀況`;
	}

	return [
		`這張圖是說明關於${title}的統計狀況`,
		'',
		...lines.slice(0, 12),
	].join('\n');
}

function buildRadialAltText({
	title, data = [], label = '數值', unit = '',
}) {
	const normalizedUnit = normalizeUnit(unit);
	const lines = data
		.filter((x) => x?.x !== undefined && x?.y !== undefined)
		.map((x) => `${x.x} 的 ${label} 是 ${formatValue(x.y)}${normalizedUnit}`);

	if (!lines.length) {
		return `這張圖是說明關於${title}的統計狀況`;
	}

	return [
		`這張圖是說明關於${title}的統計狀況`,
		'',
		...lines.slice(0, 12),
	].join('\n');
}

function flattenTreeLeaves(node, xkey, ykey) {
	if (!node) return [];
	if (!node.children?.length) {
		if (node[xkey] === undefined || node[ykey] === undefined) return [];
		return [{ x: node[xkey], y: node[ykey] }];
	}
	return node.children.flatMap((child) => flattenTreeLeaves(child, xkey, ykey));
}

function buildTreemapAltText({
	title, data, xkey, ykey, unit = '',
}) {
	const normalizedUnit = normalizeUnit(unit);
	const leaves = flattenTreeLeaves(data, xkey, ykey);
	const lines = leaves.map((x) => `${x.x} 是 ${formatValue(x.y)}${normalizedUnit}`);

	if (!lines.length) {
		return `這張圖是說明關於${title}的統計狀況`;
	}

	return [
		`這張圖是說明關於${title}的統計狀況`,
		'',
		...lines.slice(0, 12),
	].join('\n');
}

function SpecialGraph({
	Chart, shortName, width, height, name, link, data: _data, yUnit = [], downloadGraph, downloadGraphPDF, bigTitle, graphType,
}) {
	const { userState } = useContext(ContextStore);
	const [graphValue, setGraphValue] = useState();
	const refGraph = useRef();

	const data = useMemo(() => _data, [_data]);

	let xkey = 'title';
	let ykey = '';
	let c = { ...data };
	let depth = 0;
	while (c.children && depth < 10) {
		depth += 1;
		[c] = c.children;
	}
	Object.keys(c).forEach((k) => {
		if (k !== '_id' && k !== 'title' && k !== 'x') {
			if (typeof c[k] === 'number' && !k.endsWith('比例')) ykey = k;
			if (typeof c[k] === 'string') xkey = k;
		}
	});

	const altText = useMemo(() => {
		const graphTitle = name || shortName || '圖表';
		if (graphType?.toLowerCase() === 'treemap') {
			return buildTreemapAltText({
				title: graphTitle,
				data,
				xkey,
				ykey,
				unit: yUnit[0] || '',
			});
		}
		return buildRadialAltText({
			title: graphTitle,
			data: flattenTreeLeaves(data, xkey, ykey),
			label: xkey,
			unit: yUnit[0] || '',
		});
	}, [name, shortName, graphType, data, xkey, ykey, yUnit]);

	return ykey && (
		<div>
			{data?.length === 0 ? (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					innerRef={refGraph}
					bigTitle={bigTitle}
				>
					<Box h="250px" display="flex" justifyContent="center" alignItems="center">
						<Text>（無資料）</Text>
					</Box>
				</GraphContainer>
			) : (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					innerRef={refGraph}
					bigTitle={bigTitle}
					menu={(
						userState?.group >= USER_GROUP.ADMIN ? (
							<Menu placement="bottom-end">
								<MenuButton
									as={IconButton}
									size="xs"
									bgColor="rgba(200, 200, 200, 0.2)"
									aria-label={`開啟「${name || shortName || '圖表'}」操作選單`}
									icon={<Icon as={Fa} icon={faCaretDown} />}
								/>
								<MenuList>
									<MenuItem color="#333" onClick={() => downloadGraph(name, refGraph)}>下載圖表 (PNG)</MenuItem>
									<MenuItem color="#333" onClick={() => downloadGraphPDF(name, refGraph)}>下載圖表 (PDF)</MenuItem>
								</MenuList>
							</Menu>
						) : null
					)}
				>
					<>
						<Text ml={5} color="secTitle" fontSize="sm">
							{graphValue && typeof graphValue === 'string' ? (
								<>
									{graphValue}
									{' '}
									{yUnit.length ? yUnit[0] : ''}
								</>
							) : <Text visibility="hidden">請選擇</Text>}
						</Text>
						<Box w="100%" h={height}>
							<VisuallyHidden>{altText}</VisuallyHidden>
							<Chart
								role="img"
								aria-label={altText}
								hideRootNode
								animation="stiff"
								mode="squarify"
								margin={2}
								padding={2}
								colorType="literal"
								getSize={(d) => d[ykey]}
								getColor={(d) => {
									let color = '#fff';
									if (`${ykey}比例` in d) {
										const ratioKey = `${ykey}比例`;
										color = chroma.scale(['white', '#00F3FF'])(d[ratioKey]).hex();
									} else if (d[xkey]) {
										color = str2Color(d[xkey]);
									}
									return color;
								}}
								onValueMouseOver={(v) => {
									if (v[xkey]) {
										setGraphValue(v);
									}
								}}
								onValueMouseOut={() => setGraphValue(null)}
								onLeafMouseOver={(d) => {
									if (!d?.data[xkey]) {
										return;
									}
									if (d?.depth < depth - 1) {
										return;
									}
									setGraphValue(`${d?.data[xkey]}：${d?.value?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`);
								}}
								onLeafMouseOut={() => setGraphValue('')}
								data={data}
								style={{ fontSize: '.5rem', textOverflow: 'static', color: '#000000' }}
								sortFunction={(a, b) => {
									if (a.value === undefined || b.value === undefined) {
										return undefined;
									}
									return b.value - a.value;
								}}
							>
								{graphValue ? (
									<Hint value={buildValue(graphValue, xkey, ykey)}>
										<Box bg="rgba(10, 10, 10, 0.8)" color="white" p={2}>
											<Text>{buildValue(graphValue, xkey, ykey, yUnit[0]).title}</Text>
										</Box>
									</Hint>
								) : null}
							</Chart>
						</Box>
					</>
				</GraphContainer>
			)}
		</div>
	);
}

function MyRadialChart({
	shortName, width, height, name, data: _data, yUnit, link, downloadGraph, downloadGraphPDF, bigTitle,
}) {
	const { userState } = useContext(ContextStore);
	const refGraph = useRef();
	const data = useMemo(() => _data[0] ?? [], [_data]);
	const altText = useMemo(() => buildRadialAltText({
		title: name || shortName || '圖表',
		data,
		label: '類別',
		unit: yUnit?.[0] || '',
	}), [name, shortName, data, yUnit]);
	// const radialColors = ['#00F3FF', '#F4D73F', '#FD4961', '#5E3BDD', '#F0734C', '#F0F26D', '#3CDF42', '#FA8585', '#959595'];

	const legendsDisplay = useBreakpointValue({
		base: 0, md: 1,
	});
	const maxArcLinkLabelLength = useBreakpointValue({
		base: 4, lg: 10,
	});
	const defaultLegends = [
		{
			anchor: 'top-left',
			direction: 'column',
			justify: false,
			itemWidth: 100,
			itemHeight: 24,
			itemTextColor: '#999',
			itemDirection: 'left-to-right',
			itemOpacity: legendsDisplay,
			symbolSize: 18,
			symbolShape: 'circle',
			translateX: -70,
		},
	];

	return (
		<div>
			{data?.length === 0 ? (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					innerRef={refGraph}
					bigTitle={bigTitle}
				>
					<Box h="250px" display="flex" justifyContent="center" alignItems="center">
						<Text>（無資料）</Text>
					</Box>
				</GraphContainer>
			) : (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					bigTitle={bigTitle}
					innerRef={refGraph}
					menu={(
						userState?.group >= USER_GROUP.ADMIN ? (
							<Menu placement="bottom-end">
								<MenuButton
									as={IconButton}
									size="xs"
									bgColor="rgba(200, 200, 200, 0.2)"
									aria-label={`開啟「${name || shortName || '圖表'}」操作選單`}
									icon={<Icon as={Fa} icon={faCaretDown} />}
								/>
								<MenuList>
									<MenuItem color="#333" onClick={() => downloadGraph(name, refGraph)}>下載圖表 (PNG)</MenuItem>
									<MenuItem color="#333" onClick={() => downloadGraphPDF(name, refGraph)}>下載圖表 (PDF)</MenuItem>
								</MenuList>
							</Menu>
						) : null
					)}
				>
					<Box w="100%" h={height}>
						<VisuallyHidden>{altText}</VisuallyHidden>
						<ResponsivePie
							role="img"
							aria-label={altText}
							data={data.map((x, i) => ({
								id: `data${i}:${yUnit}`,
								label: x.x,
								value: x.y,
							}))}
							margin={{
								top: 20, right: 80, bottom: 20, left: 80,
							}}
							innerRadius={0.5}
							padAngle={0.7}
							colors={colors}
							cornerRadius={3}
							activeOuterRadiusOffset={8}
							borderWidth={1}
							borderColor={{
								from: 'color',
								modifiers: [
									[
										'darker',
										0.2,
									],
								],
							}}
							arcLabel={(d) => d.value?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							arcLinkLabel={(d) => (d.label?.length > maxArcLinkLabelLength ? `${d.label.substring(0, maxArcLinkLabelLength)}...` : d.label)}
							arcLinkLabelsSkipAngle={10}
							arcLinkLabelsTextColor="#f2f2f2"
							arcLinkLabelsThickness={2}
							arcLinkLabelsColor={{ from: 'color' }}
							arcLinkLabelsDiagonalLength={8}
							arcLinkLabelsStraightLength={12}
							arcLabelsSkipAngle={10}
							arcLabelsTextColor={{
								from: 'color',
								modifiers: [
									[
										'darker',
										2,
									],
								],
							}}
							legends={data.length < 10 ? defaultLegends : []}
							tooltip={(d) => (
								<Box bg="rgba(10, 10, 10, 0.8)" color="white" p={2}>
									<Text whteSpace="nowrap">{d.datum.label}</Text>
									<Text whteSpace="nowrap">
										{d.datum.value?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
										{' '}
										{d.datum.id.split(':')[1]}
									</Text>
								</Box>
							)}
						/>
					</Box>
				</GraphContainer>
			)}
		</div>
	);
}

export default function Graph(props) {
	const { rangeState, userState } = useContext(ContextStore);
	const refGraph = useRef();

	const axisDefaultStyle = {
		style: {
			line: { fill: '#999' },
			ticks: { fill: '#999' },
		},
	};
	const defaultYkeys = useMemo(() => Object.keys(props.data[0] ?? {}).filter((x) => x !== '_id' && x !== 'x' && !x.endsWith('比例')), [props.yKeys, props.data]);
	const {
		orientation = 'vertical',
		type = 'line',
		// data: _data = [],
		width = props.w ?? '100%',
		height = props.h ?? '200px',
		// name = '',
		xKey = 'x',
		yKeys: _yKeys = defaultYkeys,
		yUnit: _yUnit = '',
		showAverage = false,
		avgLeading = '平均',
		xType = 'ordinal',
		showXAxis = true,
		showYAxis = true,
		xAxisOptions = {},
		yAxisOptions = {},
		startFromZero = true,
		showHorizontalGridLines = true,
		showVerticalGridLines = false,
		// showMarks = false,
		showCrosshair = true,
		margin: _margin = {
			top: 10, left: 60, bottom: 60, right: 10,
		},
		shortName = props.shortName ?? '',
		link = props.link ?? false,
		bigTitle,
	} = props;
	const chartId = shortName;

	const [displayData, setDisplayData] = useState([true, ...Array(Math.max(_yKeys.length - 1, 0)).fill(false)]);
	const [graphValue, setGraphValue] = useState([]);
	const [data, setData] = useState([]);
	const [readyToShowGraph, setReadyToShowGraph] = useState(false);
	const [yKeys, setYKeys] = useState([]);
	const [xUnit, setXunit] = useState('');
	const [yUnit, setYUnit] = useState([]);
	const [name, setName] = useState(null);
	const [margin, setMargin] = useState(_margin);
	const [description, setDescription] = useState(null);
	const [xAxisDefaultStyle, setXAxisDefaultStyle] = useState(axisDefaultStyle);
	const { data: graphMeta } = useSWR(`/api/v1/graphs/${shortName}`);
	const { data: _data } = useSWR(`/api/v1/graph/${shortName}?start=${rangeState.start}&end=${rangeState.end}&unit=${rangeState.unit}`, { initialData: props.data ?? [] });

	// const showXAxis = useBreakpointValue({ base: false, md: _showXAxis });
	// const showXAxis = _showXAxis;

	const generateUnit = useCallback(_generateUnit, []);

	const xKeyGrinder = useCallback((_xKey) => {
		if (_xKey?.length === 3 && _xKey?.toString()?.endsWith('區')) return _xKey.slice(0, -1);
		if (_xKey === '全區域') return '全區';
		return _xKey?.toString() || '';
	}, []);

	const downloadGraph = async (graphName = 'graph', ref = null) => {
		if (!ref.current) return;
		await sleep(100);
		const canvas = await html2canvas(ref.current, {
			backgroundColor: '#001449',
			logging: false,
			useCORS: true,
			scale: 2,
		});
		const imgData = canvas.toDataURL();

		const graphLink = document.createElement('a');
		graphLink.download = `${graphName}.png`;
		graphLink.href = imgData;
		graphLink.click();
	};

	const downloadGraphPDF = async (graphName = 'graph', ref = null) => {
		if (!ref.current) return;
		await sleep(100);
		const canvas = await html2canvas(ref.current, {
			backgroundColor: '#001449',
			logging: false,
			useCORS: true,
			scale: 2,
		});
		const imgData = canvas.toDataURL();

		saveGraphPdf({
			imageData: imgData,
			width: ref.current.clientWidth,
			height: ref.current.clientHeight,
			filename: graphName,
		});
	};

	useEffect(() => {
		if (_data) {
			const yK = [];
			const yU = [];
			const d = _yKeys
				.map((yKey) => _data
					?.filter((x) => (
						!x.x?.toString()?.includes('臺北市') && !x.x?.toString()?.includes('桃園市')
					))?.map((x) => ({
						x: xKeyGrinder(x[xKey]),
						y: x[yKey],
					})))
				.filter((x, i) => {
					if (displayData[i]) {
						const unit = _yUnit[i] || generateUnit(_yKeys[i]);
						yK.push(_yKeys[i]);
						yU.push(unit);
						return true;
					}
					return false;
				})
				.filter((x) => x !== null);
			const isBarChart = type.toLowerCase() === 'bar' || type.toLowerCase() === 'horizontalbar' || type.toLowerCase() === 'verticalbar';
			setData(isBarChart && d.length === 1 ? d.map((dataset) => stable(dataset, (a, b) => b.y - a.y)) : d);
			setYKeys(yK);
			setYUnit(yU);

			if (document) {
				const totalWidth = (refGraph?.current?.clientWidth || 0) - 120;

				if (totalWidth > 0) {
					const fragment = document.createDocumentFragment();
					const canvas = document.createElement('canvas');
					fragment.appendChild(canvas);
					const ctx = canvas.getContext('2d');
					let maximumWidth = Number.MIN_SAFE_INTEGER;
					let maximumRotatedWidth = Number.MIN_SAFE_INTEGER;
					d.forEach((s) => s.forEach((x) => {
						maximumWidth = Math.max(maximumWidth, ctx.measureText(x?.x?.toString() ?? '', '12px Noto Sans TC').width);
						maximumRotatedWidth = Math.max(maximumRotatedWidth, ctx.measureText(x?.x?.toString()?.slice(0, 4) ?? '', '12px Noto Sans TC').width);
					}));

					maximumRotatedWidth *= Math.cos(45 * (Math.PI / 180));
					const avgWidth = totalWidth / (d?.[0]?.length || 1);

					const ratio = Math.max(Math.ceil(maximumRotatedWidth / avgWidth), 1);
					if (maximumWidth > avgWidth) {
						setXAxisDefaultStyle({
							...axisDefaultStyle,
							// tickFormat: (v, i) => (i % 4 === 0 ? v : '· '),
							tickFormat: (v, i) => {
								if (i % ratio !== 0) return '· ';
								return (v.length > 4 ? `${v.slice(0, 4)}...` : v);
							},
							tickLabelAngle: -45,
						});
						setMargin({
							...margin,
							bottom: 60,
						});
					}
				}
			} else if (d && d[0] && d[0].length > 15) {
				setXAxisDefaultStyle({
					...axisDefaultStyle,
					// tickFormat: (v, i) => (i % 4 === 0 ? v : '· '),
					tickFormat: (v) => (v.length > 4 ? `${v.slice(0, 4)}...` : v),
					tickLabelAngle: -45,
				});
				setMargin({
					...margin,
					bottom: 60,
				});
			} else {
				setXAxisDefaultStyle(axisDefaultStyle);
				setMargin(_margin);
			}

			if (!_yKeys?.length && _yUnit) {
				setYUnit([_yUnit]);
			}

			if (d && d[0] && d[0].map((x) => x.x).every((x) => typeof x === 'number')) {
				if (rangeState.unit === 'y') {
					setXunit(' 年');
				}
			} else {
				setXunit('');
			}

			setTimeout(() => { setReadyToShowGraph(true); }, 500);
		}
	}, [_data, xKey, _yKeys, displayData, refGraph?.current?.clientWidth]);

	useEffect(() => {
		if (graphMeta) {
			if (graphMeta.name) {
				setName(graphMeta.name);
			}
			if (graphMeta.description) {
				setDescription(graphMeta.description);
			}
		}
	}, [graphMeta]);

	const altText = useMemo(() => buildLineBarAltText({
		title: name || shortName || '圖表',
		data,
		yKeys,
		yUnit,
	}), [name, shortName, data, yKeys, yUnit]);

	let Chart;
	switch (type.toString().toLowerCase()) {
	case 'sunburst':
		Chart = makeVisFlexible(Sunburst);
		return (
			<SpecialGraph
				Chart={Chart}
				width={width}
				height={height}
				name={name}
				description={description}
				data={_data}
				yUnit={yUnit}
				margin={margin}
				shortName={shortName}
				link={link}
				downloadGraph={downloadGraph}
				downloadGraphPDF={downloadGraphPDF}
				bigTitle={bigTitle}
				graphType={type}
			/>
		);
	case 'treemap':
		Chart = makeVisFlexible(Treemap);
		return (
			<SpecialGraph
				Chart={Chart}
				width={width}
				height={height}
				name={name}
				description={description}
				data={_data}
				yUnit={yUnit}
				margin={margin}
				shortName={shortName}
				link={link}
				downloadGraph={downloadGraph}
				downloadGraphPDF={downloadGraphPDF}
				bigTitle={bigTitle}
				graphType={type}
			/>
		);
	case 'radial':
		Chart = makeVisFlexible(RadialChart);
		return (
			<MyRadialChart
				Chart={Chart}
				width={width}
				height={height}
				name={name}
				description={description}
				data={data}
				yKey={yKeys[0]}
				yUnit={yUnit}
				margin={margin}
				shortName={shortName}
				link={link}
				downloadGraph={downloadGraph}
				downloadGraphPDF={downloadGraphPDF}
				bigTitle={bigTitle}
			/>
		);
	case 'bar':
	case 'verticalbar':
	case 'horizontalbar':
		if (orientation.toString().toLowerCase() === 'h' || orientation.toString().toLowerCase() === 'horizontal') {
			Chart = HorizontalBarSeries;
		} else {
			Chart = VerticalBarSeries;
		}
		break;
	case 'line':
	default:
		Chart = LineMarkSeries;
	}

	if (xType === 'linear' && !xAxisOptions.tickFormat) {
		xAxisOptions.tickFormat = (v) => (v % 1 === 0 ? v : '');
	}
	yAxisOptions.tickFormat = bigNum2Chinese;

	const allChecked = displayData.every(Boolean);
	const isIndeterminate = displayData.some(Boolean) && !allChecked;
	const checkboxIdBase = `graph-filter-${shortName || 'default'}`;

	return (
		<div>
			{data[0]?.length === 0 ? (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					innerRef={refGraph}
					bigTitle={bigTitle}
				>
					<Box h="250px" display="flex" justifyContent="center" alignItems="center">
						<Text>（無資料）</Text>
					</Box>
				</GraphContainer>
			) : (
				<GraphContainer
					shortName={shortName}
					link={link}
					width={width}
					innerRef={refGraph}
					bigTitle={bigTitle}
					menu={(
						<ButtonGroup data-html2canvas-ignore>
							<Popover placement="top-end">
								<PopoverTrigger>
									<Button
										size="xs"
										px={0}
										bgColor="rgba(200, 200, 200, 0.2)"
										aria-label={`開啟「${name || shortName || '圖表'}」圖表欄位篩選`}
									>
										<Icon as={Fa} icon={faFilter} color="primary" />
									</Button>
								</PopoverTrigger>
								<PopoverContent
									rootProps={{ zIndex: 1020 }}
								>
									<PopoverArrow />
									<PopoverCloseButton color="background" />
									{/* <PopoverHeader></PopoverHeader> */}
									<PopoverBody color="background">
										<Box as="fieldset" border="0" m={0} p={0} minW={0}>
											<Box as="legend" fontSize="sm" mb={1}>
												顯示在圖表上的欄位
											</Box>
											{_yKeys.length > 1 ? (
												<Box display="flex" alignItems="center" gap={2}>
													<Checkbox
														id={`${checkboxIdBase}-all`}
														data-testid={`${checkboxIdBase}-all`}
														size="sm"
														isChecked={allChecked}
														isIndeterminate={isIndeterminate}
														onChange={(e) => setDisplayData([...displayData.fill(e.target.checked)])}
													/>
													<Box as="label" htmlFor={`${checkboxIdBase}-all`} fontSize="sm" cursor="pointer">
														全選
													</Box>
												</Box>
											) : null}
											<Stack pl={4} mt={1} spacing={0}>
												{_yKeys.map((key, i) => (
													<Box key={`selection-${key}`}>
														<Box display="flex" alignItems="center" gap={2}>
															<Checkbox
																id={`${checkboxIdBase}-${i}`}
																data-testid={`${checkboxIdBase}-${i}`}
																size="sm"
																isChecked={displayData[i]}
																onChange={(e) => setDisplayData([...displayData.slice(0, i), e.target.checked, ...displayData.slice(i + 1)])}
															/>
															<Box as="label" htmlFor={`${checkboxIdBase}-${i}`} fontSize="sm" cursor="pointer">
																{key}
															</Box>
														</Box>
													</Box>
												))}
											</Stack>
										</Box>
									</PopoverBody>
								</PopoverContent>
							</Popover>
							{userState?.group >= USER_GROUP.ADMIN ? (
								<Menu placement="bottom-end">
									<MenuButton
										as={IconButton}
										size="xs"
										bgColor="rgba(200, 200, 200, 0.2)"
										aria-label={`開啟「${name || shortName || '圖表'}」操作選單`}
										icon={<Icon as={Fa} icon={faCaretDown} />}
									/>
									<MenuList>
										<MenuItem color="#333" onClick={() => downloadGraph(name, refGraph)}>下載圖表 (PNG)</MenuItem>
										<MenuItem color="#333" onClick={() => downloadGraphPDF(name, refGraph)}>下載圖表 (PDF)</MenuItem>
									</MenuList>
								</Menu>
							) : null}
						</ButtonGroup>
					)}
				>

					{showAverage ? (
						<Text ml={5} color="subtitle" fontSize="sm">
							{data.length === 1 ? (
								<>
									{avgLeading}
									{' '}
									{Math.floor(data[0].reduce((acc, x) => acc + x.y, 0) / data[0].length).toString()
										.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
									{' '}
									{yUnit[0]}
								</>
							) : (
								<Box visibility="hidden">平均</Box>
							)}
						</Text>
					) : null}
					<Box w="100%" h={height} minHeight="100px" key={hash(data)}>
						<VisuallyHidden>{altText}</VisuallyHidden>
						<FlexibleXYPlot
							role="img"
							aria-label={altText}
							margin={margin}
							onMouseLeave={() => setGraphValue([])}
							xType={xType}
							yDomain={startFromZero ? [0, data.reduce((acc, dataset) => Math.max(acc, Math.max(...dataset.map((d) => d.y))), 0) * 1.1] : undefined}
							animation={{ duration: 50000 }}
						>
							{showHorizontalGridLines ? <HorizontalGridLines /> : null}
							{showVerticalGridLines ? <VerticalGridLines /> : null}
							{showXAxis ? (
								<XAxis
									{...xAxisOptions}
									{...{
										...xAxisDefaultStyle,
										...(xAxisOptions?.style || {}),
									}}
								/>
							) : null}
							{showYAxis ? (
								<YAxis
									{...yAxisOptions}
									{...{
										...axisDefaultStyle,
										...(xAxisOptions?.style || {}),
									}}
								/>
							) : null}
							{/* { showMarks ? <MarkSeries data={graphValue} color="#999" /> : null } */}
							{showCrosshair ? (
								<Crosshair values={graphValue}>
									<Box bg="rgba(10, 10, 10, 0.8)" color="white" p={2}>
										<Text whiteSpace="nowrap">
											{graphValue[0]?.x}
											{xUnit}
										</Text>
										{data.map((x, i) => (
											<Text key={`tooltip${chartId}${i}`} whiteSpace="nowrap">
												<Box d="inline-block" w="8px" h="8px" mr={1} bg={colors[i]} />
												{yKeys[i]}
												：
												{graphValue[i]?.y?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
												{' '}
												{yUnit[i]}
											</Text>
										))}
									</Box>
								</Crosshair>
							) : null}
							{type === 'line' && data.length === 1 ? (
								<AreaSeries
									data={!readyToShowGraph ? data[0].map((d) => ({ ...d, y: 0 })) : data[0]}
									color={chroma(colors[0]).brighten(2).hex()}
									opacity={0.5}
									animation={false}
								/>
							) : null}
							{data.map((dataset, i) => (
								<Chart
									size={2}
									key={`chart${chartId}${i}`}
									color={colors[i]}
									animation
									onNearestX={(value, { index }) => setGraphValue(data.map((d) => d[index]))}
									data={!readyToShowGraph ? dataset.map((d) => ({ ...d, y: 0 })) : dataset}
									style={type === 'line' && data.length > 1 ? { strokeWidth: 3 } : { strokeWidth: 2 }}
								/>
							))}
						</FlexibleXYPlot>
					</Box>
				</GraphContainer>
			)}
		</div>
	);
}
